import { Controller } from "@hotwired/stimulus"

import { pushGa4Event } from "@/helpers/ga4_helper"
import Cookies from "@/helpers/cookies"

const MINUTE_IN_MS = 60 * 1000
const HOUR_IN_MS = 60 * MINUTE_IN_MS
const DAY_IN_MS = 24 * HOUR_IN_MS
const SLIDE_INTERVAL_IN_MS = 7 * 1000
const PROMO_COOKIE = "sitewide_promotion"

export default class CampaignController extends Controller<HTMLElement> {
  static targets = ["slide", "dotInput", "timer", "days", "hours", "minutes", "sitewidePromo"]
  declare readonly slideTargets: HTMLElement[]
  declare readonly dotInputTargets: HTMLInputElement[]
  declare readonly timerTargets: HTMLElement[]
  declare readonly daysTargets: HTMLElement[]
  declare readonly hoursTargets: HTMLElement[]
  declare readonly minutesTargets: HTMLElement[]
  declare readonly sitewidePromoTarget: HTMLElement
  declare readonly hasSitewidePromoTarget: boolean

  static values = {
    endsAt: String,
    promoName: String
  }
  declare readonly endsAtValue: string
  declare readonly promoNameValue: string

  private countdownId?: number
  private slideId?: number

  connect(): void {
    if (this.endsAtValue) {
      this.renderCountdown()
      this.countdownId = window.setInterval(() => this.renderCountdown(), MINUTE_IN_MS)
    }

    this.startSlideshow()

    // Presence of element is sufficient to trigger a promo "view"
    if (this.hasSitewidePromoTarget) {
      pushGa4Event({
        event: "view_promotion",
        promotion_name: this.promoNameValue
      })
    }
  }

  disconnect(): void {
    this.stopCountdown()
    this.stopSlideshow()
  }

  select(): void {
    this.toggleSlides()
    this.restartSlideshow()
  }

  hideSitewidePromo(): void {
    this.sitewidePromoTarget.hidden = true
    Cookies.setAtUserRequest(PROMO_COOKIE, "hide")
    document.body.style.setProperty("--promo-bar-height", "0px")
  }

  clickOnSitewidePromo({ target }: Event): void {
    if ((target as HTMLElement).closest("a")) {
      pushGa4Event({ event: "select_promotion", promotion_name: this.promoNameValue })
    }
  }

  private renderCountdown(): void {
    const endsAt = new Date(this.endsAtValue).getTime()
    const remainingInMs = endsAt - Date.now()

    if (remainingInMs <= 0) {
      this.timerTargets.forEach((timer) => (timer.textContent = "00d : 00h : 00m"))
      this.daysTargets.forEach((day) => (day.textContent = "00"))
      this.hoursTargets.forEach((hour) => (hour.textContent = "00"))
      this.minutesTargets.forEach((minute) => (minute.textContent = "00"))
      this.stopCountdown()

      return
    }

    const days = Math.floor(remainingInMs / DAY_IN_MS)
    const hours = Math.floor((remainingInMs % DAY_IN_MS) / HOUR_IN_MS)
    const minutes = Math.floor((remainingInMs % HOUR_IN_MS) / MINUTE_IN_MS)

    this.timerTargets.forEach(
      (timer) =>
        (timer.textContent = `${this.pad(days)}d : ${this.pad(hours)}h : ${this.pad(minutes)}m`)
    )
    this.daysTargets.forEach((day) => (day.textContent = this.pad(days)))
    this.hoursTargets.forEach((hour) => (hour.textContent = this.pad(hours)))
    this.minutesTargets.forEach((minute) => (minute.textContent = this.pad(minutes)))
  }

  private pad(value: number): string {
    return String(value).padStart(2, "0")
  }

  private stopCountdown(): void {
    if (this.countdownId) {
      window.clearInterval(this.countdownId)
      this.countdownId = undefined
    }
  }

  private toggleSlides(): void {
    this.slideTargets.forEach((slide) => slide.classList.toggle("-translate-x-full"))
  }

  private showNextSlide(): void {
    this.dotInputTargets.find((input) => !input.checked)?.click()
  }

  private startSlideshow(): void {
    if (this.slideTargets.length > 1) {
      this.slideId = window.setInterval(() => this.showNextSlide(), SLIDE_INTERVAL_IN_MS)
    }
  }

  private stopSlideshow(): void {
    if (this.slideId) {
      window.clearInterval(this.slideId)
      this.slideId = undefined
    }
  }

  private restartSlideshow(): void {
    this.stopSlideshow()
    this.startSlideshow()
  }
}
